/**
 * Agregacion temporal: mes, trimestre, semestre y año.
 *
 * El backend devuelve SIEMPRE la serie mensual de doce posiciones. Agrupar es
 * cosa de la pantalla, y por una razon concreta: el usuario cambia de
 * granularidad varias veces por minuto mientras mira, y volver al servidor por
 * cada cambio convierte un gesto de lectura en una espera.
 *
 * SUMAR NO SIEMPRE ES AGREGAR. Un importe se suma (los ingresos del trimestre
 * son la suma de sus meses), pero un SALDO no: la caja acumulada de un
 * trimestre es la del ultimo mes, no la suma de los tres. Por eso `agrupar`
 * pide explicitamente como combinar y no asume `sum`: la version que asumia
 * suma daba una caja acumulada cuatro veces mas grande de lo real y parecia
 * plausible.
 */

export type Granularidad = "mes" | "trimestre" | "semestre" | "anio"

export type Combinacion = "suma" | "ultimo"

export interface Bucket {
  /** Etiqueta corta para la cabecera de la tabla: "Ene", "Q1", "S1", "2026". */
  label: string
  /** Nombre largo, para `title` y lectores de pantalla. */
  nombre: string
  /** Meses 1-12 que caen en este bucket. */
  meses: number[]
  valor: number
}

const MES_CORTO = ["Ene", "Feb", "Mar", "Abr", "May", "Jun",
                   "Jul", "Ago", "Set", "Oct", "Nov", "Dic"]

const MES_LARGO = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                   "Julio", "Agosto", "Setiembre", "Octubre", "Noviembre", "Diciembre"]

/** Los meses (1-12) que componen cada bucket de una granularidad. */
export function bucketsDe(granularidad: Granularidad, anio: number): Array<Omit<Bucket, "valor">> {
  switch (granularidad) {
    case "mes":
      return MES_CORTO.map((label, i) => ({
        label, nombre: `${MES_LARGO[i]} ${anio}`, meses: [i + 1],
      }))
    case "trimestre":
      return [0, 1, 2, 3].map((q) => ({
        label: `Q${q + 1}`,
        nombre: `Trimestre ${q + 1} de ${anio}`,
        meses: [q * 3 + 1, q * 3 + 2, q * 3 + 3],
      }))
    case "semestre":
      return [0, 1].map((s) => ({
        label: `S${s + 1}`,
        nombre: `Semestre ${s + 1} de ${anio}`,
        meses: Array.from({ length: 6 }, (_, i) => s * 6 + i + 1),
      }))
    case "anio":
      return [{
        label: String(anio),
        nombre: `Año ${anio}`,
        meses: Array.from({ length: 12 }, (_, i) => i + 1),
      }]
  }
}

/**
 * Agrupa una serie mensual de 12 posiciones (indice 0 = enero).
 *
 * `combinacion`:
 *   - `suma`   para importes (ingresos, gastos, neto del mes);
 *   - `ultimo` para saldos (caja acumulada), donde el valor del periodo es el
 *     del ULTIMO mes que lo compone.
 */
export function agrupar(
  serie: number[],
  granularidad: Granularidad,
  anio: number,
  combinacion: Combinacion = "suma",
): Bucket[] {
  return bucketsDe(granularidad, anio).map((b) => {
    const valores = b.meses.map((m) => serie[m - 1] ?? 0)
    const valor = combinacion === "ultimo"
      ? valores[valores.length - 1] ?? 0
      : valores.reduce((s, v) => s + v, 0)
    return { ...b, valor }
  })
}

export const GRANULARIDADES: Array<{ id: Granularidad; label: string }> = [
  { id: "mes", label: "Mensual" },
  { id: "trimestre", label: "Trimestral" },
  { id: "semestre", label: "Semestral" },
  { id: "anio", label: "Anual" },
]

/* ==========================================================================
   LA LÍNEA QUE SEPARA LO REAL DEL FORECAST
   ========================================================================== */

/**
 * En qué bucket cae la frontera entre lo real y lo proyectado.
 *
 * El backend da el CORTE como un mes (`corte_real`, el último con asientos en
 * el libro gerencial). La tabla, en cambio, puede estar agrupada por trimestre,
 * semestre o año, y ahí la pregunta cambia: **¿de qué lado de la línea cae un
 * trimestre que tiene dos meses reales y uno proyectado?**
 *
 * La respuesta es **del lado del forecast**, y no es una preferencia: un
 * trimestre cuya cifra incluye un mes sin respaldo es una cifra sin respaldo.
 * Contarlo como real sería exactamente lo que la línea existe para impedir —
 * dar por cerrado un número que todavía puede moverse.
 *
 * Devuelve el ÍNDICE del último bucket completamente real, o `-1` si ninguno lo
 * es. `-1` es un valor legítimo y frecuente: en granularidad anual, un año con
 * once meses asentados no tiene ni un bucket real.
 *
 * `corteMes` nulo devuelve `null`, que la pantalla distingue de `-1`: «no hay
 * con qué comparar» y «nada de esto está respaldado todavía» no se dicen igual.
 */
export function ultimoBucketReal(
  buckets: Array<Pick<Bucket, "meses">>,
  corteMes: number | null | undefined,
): number | null {
  if (corteMes == null) return null
  let ultimo = -1
  for (let i = 0; i < buckets.length; i++) {
    // Completamente real: TODOS sus meses están en o antes del corte.
    if (buckets[i].meses.every((m) => m <= corteMes)) ultimo = i
    else break
  }
  return ultimo
}
